// src/components/nodes/NodeBase.js
import { Handle, Position, useStore } from 'reactflow';
import { useState, useEffect } from 'react';

/**
 * 节点类型基类 - 增强版
 */
export class NodeBase {
  constructor(type, defaultData = {}) {
    this.type = type;
    this.defaultData = defaultData;
  }

  /**
   * 渲染节点内容（子类必须实现）
   */
  renderContent(data, selected) {
    throw new Error('子类必须实现 renderContent 方法');
  }

  /**
   * 渲染动态连接点
   */
  renderDynamicHandles() {
    const DynamicHandles = ({ nodeId }) => {
      const [isHovered, setIsHovered] = useState(false);
      const [isConnecting, setIsConnecting] = useState(false);
      
      // 监听连接状态
      const connectionStartNodeId = useStore((state) => 
        state.connectionStartHandle?.nodeId
      );
      
      // 更新连接状态
      useEffect(() => {
        setIsConnecting(!!connectionStartNodeId);
      }, [connectionStartNodeId]);

      // 定义所有连接点位置
      const handlePositions = [
        { position: Position.Top, type: 'target', id: 'top-target' },
        { position: Position.Top, type: 'source', id: 'top-source' },
        { position: Position.Bottom, type: 'target', id: 'bottom-target' },
        { position: Position.Bottom, type: 'source', id: 'bottom-source' },
        { position: Position.Left, type: 'target', id: 'left-target' },
        { position: Position.Left, type: 'source', id: 'left-source' },
        { position: Position.Right, type: 'target', id: 'right-target' },
        { position: Position.Right, type: 'source', id: 'right-source' },
      ];

      return (
        <>
          {handlePositions.map(({ position, type, id }) => {
            let shouldShow = false;
            
            if (isConnecting) {
              // 连接中：当前节点显示目标点，其他节点显示目标点
              if (connectionStartNodeId === nodeId) {
                // 当前节点：显示目标点
                shouldShow = type === 'target';
              } else {
                // 其他节点：显示目标点
                shouldShow = type === 'target';
              }
            } else {
              // 非连接状态：悬停时显示源点
              shouldShow = isHovered && type === 'source';
            }

            return (
              <Handle
                key={id}
                id={id}
                type={type}
                position={position}
                className={`node-handle ${type}-handle`}
                style={{
                  opacity: shouldShow ? 1 : 0,
                  transition: 'opacity 0.2s ease',
                  background: type === 'source' ? '#ff0072' : '#007bff',
                  border: '2px solid white',
                  width: '8px',
                  height: '8px',
                  zIndex: 10,
                }}
              />
            );
          })}
          
          {/* 悬停检测区域 */}
          <div 
            className="handle-hover-detector"
            style={{
              position: 'absolute',
              top: -10,
              left: -10,
              right: -10,
              bottom: -10,
              zIndex: 5,
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          />
        </>
      );
    };

    return DynamicHandles;
  }

  /**
   * 创建节点组件
   */
  createComponent(customProps = {}) {
    const DynamicHandles = this.renderDynamicHandles();
    
    const NodeComponent = ({ data, selected, id, ...props }) => {
      const mergedProps = { ...customProps, ...props };
      
      return (
        <div 
          className={`${this.type}-node ${selected ? 'selected' : ''}`}
          style={{ position: 'relative' }}
        >
          <DynamicHandles nodeId={id} />
          {this.renderContent(data, selected, mergedProps)}
        </div>
      );
    };

    NodeComponent.displayName = `${this.type.charAt(0).toUpperCase() + this.type.slice(1)}Node`;
    return NodeComponent;
  }

  /**
   * 获取节点配置信息
   */
  getConfig() {
    return {
      type: this.type,
      label: this.type.charAt(0).toUpperCase() + this.type.slice(1) + ' Node',
      icon: '⬜',
      defaultData: this.defaultData
    };
  }
}